import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useState, useEffect } from 'react';
import { FiAlertCircle } from 'react-icons/fi';
import { cn } from "@/lib/utils";

const BrowserAgentConfig = ({ stepKey, updateStepData, getStepData }) => {
    const stepData = getStepData(stepKey);
    const [urlError, setUrlError] = useState('');
    const [localUrl, setLocalUrl] = useState(stepData.url || '');

    const validateUrl = (url) => {
        if (!url) {
            setUrlError('URL is required');
            return false;
        }

        // Check if URL has protocol
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
            setUrlError('URL must start with http:// or https://');
            return false;
        }

        // More specific URL pattern validation
        const urlPattern = /^https?:\/\/[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}(\/\S*)?$/;
        if (!urlPattern.test(url)) {
            setUrlError('Please enter a valid URL (e.g., https://example.com)');
            return false;
        }

        try {
            new URL(url);
            setUrlError('');
            return url;
        } catch (e) {
            setUrlError('Please enter a valid URL (e.g., https://example.com)');
            return false;
        }
    };

    const handleUrlChange = (e) => {
        const { value } = e.target;
        setLocalUrl(value);
    };

    const handleUrlBlur = (e) => {
        const { value } = e.target;
        const formattedUrl = validateUrl(value);
        if (formattedUrl) {
            updateStepData(stepKey, { ...stepData, url: formattedUrl });
        }
    };

    // Update local URL when stepData changes
    useEffect(() => {
        setLocalUrl(stepData.url || '');
    }, [stepData.url]);

    // Validate URL on component mount if it exists
    useEffect(() => {
        if (stepData.url) {
            validateUrl(stepData.url);
        }
    }, [stepData.url]);

    return (
        <>
            <div className="flex flex-col space-y-3">
                <Label htmlFor="url">URL to monitor</Label>
                <div className="space-y-1">
                    <Input
                        id="url" 
                        name="url"
                        value={localUrl}
                        onChange={handleUrlChange}
                        onBlur={handleUrlBlur}
                        placeholder="https://example.com"
                        className={cn(
                            urlError && "border-red-500 focus-visible:ring-red-500"
                        )}
                    />
                    {urlError && (
                        <div className="flex items-center gap-1 text-sm text-red-500">
                            <FiAlertCircle className="w-4 h-4 flex-shrink-0" />
                            {urlError}
                        </div>
                    )}
                </div>
                <p className="text-xs text-muted-foreground">
                    The URL where the browser agent will start its monitoring.
                </p>
            </div>
            <div className="flex flex-col space-y-3 mt-6">
                <Label htmlFor="prompt">Monitoring Instructions</Label>
                <Textarea
                    id="prompt"
                    name="prompt"
                    value={stepData.prompt || ''}
                    onChange={(e) => {
                        const { name, value } = e.target;
                        updateStepData(stepKey, { ...stepData, [name]: value });
                    }}
                    placeholder="Check if the login button is visible and clickable"
                    className="min-h-32"
                />
                <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">
                        Keep instructions simple. The browser agent will attempt to complete this task and report back.
                    </p>
                    <div className="text-xs text-muted-foreground space-y-1">
                        <p className="font-medium">Good examples:</p>
                        <ul className="list-disc list-inside space-y-0.5">
                            <li>"Click the 'Sign In' button and verify the login form appears"</li>
                            <li>"Check if the 'Add to Cart' button is visible and enabled"</li>
                            <li>"Verify the price is displayed and matches $99.99"</li>
                        </ul>
                    </div>
                </div>
            </div>
        </>
    );
};

export default BrowserAgentConfig; 